import { defineMessage, FormattedMessage, MessageDescriptor } from 'react-intl'
import { generatePath } from 'react-router'

import { BusinessRoute } from '@/constants/paths'
import { GoBackButton } from '@/shared/components'
import { ChevronRight } from '@/shared/icons/outline'
import { Card, SlideInScreen, Typography } from '@/shared/ui'

import { OnboardingStep, OnboardingStepConfig } from '../../types'
import { getPreviousStep } from '../../utils/getPreviousStep'

type BusinessAddressType = 'same-address' | 'different-address'

type BusinessAddressOption = {
  title: MessageDescriptor
  description: MessageDescriptor
  type: BusinessAddressType
}

const BUSINESS_ADDRESS_OPTIONS: BusinessAddressOption[] = [
  {
    title: defineMessage({
      id: 'businessAddress.type.same.title',
      defaultMessage: 'Physical address is the same as the Registered address',
    }),
    description: defineMessage({
      id: 'businessAddress.type.same.description',
      defaultMessage:
        'I will only enter one address information and a proof of address support document (for example; a utility bill)',
    }),
    type: 'same-address',
  },
  {
    title: defineMessage({
      id: 'businessAddress.different.title',
      defaultMessage: 'Add two different addresses details',
    }),
    description: defineMessage({
      id: 'businessAddress.different.description',
      defaultMessage:
        'Provide Registered address and then Physical address with a proof of address support document for the Physical one (for example; a utility bill)',
    }),
    type: 'different-address',
  },
]

type Props = {
  steps: OnboardingStep[]
  config?: OnboardingStepConfig
  onSelect: (type: BusinessAddressType) => void
}

export const SelectBusinessAddressType = ({
  steps,
  config,
  onSelect,
}: Props) => {
  return (
    <>
      <GoBackButton
        className="hidden md:left-80 md:flex"
        to={generatePath(BusinessRoute.OnboardingStep, {
          step: getPreviousStep(steps, config),
        })}
      />

      <SlideInScreen>
        <Typography variant="h3" text="center">
          <FormattedMessage
            defaultMessage="Business address"
            id="onboarding.businessAddressSelect.title"
          />
        </Typography>

        <div className="p-2" />

        <Typography text="center">
          <FormattedMessage
            defaultMessage="Is the company's physical address the same as the registered address?"
            id="onboarding.businessAddressSelect.description"
          />
        </Typography>

        <div className="p-6" />

        <div className="flex flex-col gap-4">
          {BUSINESS_ADDRESS_OPTIONS.map((option) => (
            <Card
              key={option.title.id}
              size="medium"
              className="flex w-full cursor-pointer items-center justify-between"
              asChild
              scalable
            >
              <button
                onClick={() => {
                  onSelect(option.type)
                }}
              >
                <div className="flex flex-col gap-1 pr-12">
                  <Typography bold>
                    <FormattedMessage {...option.title} />
                  </Typography>

                  <Typography
                    variant="body-small"
                    className="text-neutral-gray-600"
                  >
                    <FormattedMessage {...option.description} />
                  </Typography>
                </div>

                <ChevronRight className="text-neutral-gray-600 size-7 shrink-0" />
              </button>
            </Card>
          ))}
        </div>
      </SlideInScreen>
    </>
  )
}
